import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import SyncIcon from '@material-ui/icons/Sync';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import CampaignSpendTable from '../../components/marketing/CampaignSpendTable';
import {
    metaCityDailyCacheKey,
    metaReportCacheKey,
} from '../../components/marketing/marketingAdsCacheKey';
import { setMarketingAdsFilters, setMarketingAdsMeta } from '../../redux/actions/marketingAdsAction';
import MetaAdsCampaignLoader from '../../components/marketing/MetaAdsCampaignLoader';
import MarketingSpendOverviewAccordion from '../../components/marketing/MarketingSpendOverviewAccordion';
import MetaAdsLeadDialog from '../../components/marketing/MetaAdsLeadDialog';
import SpendTrendChart from '../../components/marketing/SpendTrendChart';
import {
    ZONES,
    campaignMatchesZones,
    citiesMatchingZones,
    mergeDailyTrends,
    zoneForCity,
} from '../../components/marketing/cityZones';
import { escapeCsv, formatMetric, formatMoney, formatNumber, localDate } from '../../components/marketing/format';
import {
    adsErrorMessage,
    getAdCities,
    getCampaignReport,
    getCityDailyBreakdown,
    getLeadTargets,
    getSpendTrend,
    saveLeadTarget,
    isCanceled,
    normalizeCampaigns,
    normalizeDaily,
    syncCampaignReport,
    campaignGroupCityTags,
    getMetaAdsLeadDetails,
    getMetaAdsBucketTotals,
} from '../../services/metaAdsService';
import { downloadLeadRowsCsv } from '../../components/marketing/MetaAdsLeadDialog';
import MetaPlatformSwitch from '../../components/marketing/MetaPlatformSwitch';
import LeadChannelSwitch from './LeadChannelSwitch';

const LEAD_BUCKET_LABEL = {
    received: 'Leads received',
    sql: 'SQL-Real',
    tbc: 'Sales-TBC',
    real: 'Total Real',
    won: 'Sales Won',
};

const useStyles = makeStyles(() => ({
    root: {
        backgroundColor: '#f3f5f8',
        minHeight: '100%',
        padding: '4px 4px 24px',
    },
    page: {
        maxWidth: 1440,
        margin: '0 auto',
    },
    headerCard: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 16,
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: '20px 24px',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
    },
    title: {
        fontSize: 24,
        fontWeight: 700,
        color: '#1C375D',
        margin: 0,
    },
    subtitle: {
        fontSize: 13,
        color: '#8a92a3',
        marginTop: 6,
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
    },
    syncBtn: {
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        padding: '8px 18px',
        boxShadow: 'none',
        '&:hover': { backgroundColor: '#152a47', boxShadow: 'none' },
        '&.Mui-disabled': { backgroundColor: '#9aa8bc', color: '#f5f5f5' },
    },
    exportBtn: {
        backgroundColor: '#355025',
        color: '#FFF2EB',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        padding: '8px 18px',
        boxShadow: 'none',
        '&:hover': { backgroundColor: '#28401c', boxShadow: 'none' },
        '&.Mui-disabled': { backgroundColor: '#9aa8bc', color: '#f5f5f5' },
    },
    linkBtn: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        color: '#1C375D',
    },
    filters: {
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
        alignItems: 'center',
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 16,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
    },
    cityField: {
        minWidth: 220,
    },
    zoneField: {
        minWidth: 220,
    },
    alert: {
        padding: '12px 16px',
        borderRadius: 8,
        marginBottom: 16,
        fontSize: 14,
    },
    error: {
        background: '#fff1f0',
        color: '#a61b1b',
    },
    success: {
        background: '#edf9f0',
        color: '#176b35',
    },
    footnote: {
        margin: '8px 0 16px',
        color: '#8a92a3',
        fontSize: 12,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 16,
        marginBottom: 8,
    },
    card: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: '16px 18px',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
        minWidth: 0,
    },
    icon: {
        width: 36,
        height: 36,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1C375D14',
        color: '#1C375D',
        flexShrink: 0,
    },
    label: {
        fontSize: 11.5,
        color: '#8a92a3',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    },
    value: {
        fontSize: 20,
        fontWeight: 700,
        color: '#1C375D',
        marginTop: 2,
        overflowWrap: 'anywhere',
    },
    loaderCard: {
        backgroundColor: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
        marginBottom: 16,
    },
}));

function SummaryCard({ classes, icon, label, value, onClick }) {
    const clickable = typeof onClick === 'function';
    return (
        <article
            className={classes.card}
            onClick={clickable ? onClick : undefined}
            onKeyDown={clickable ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick();
                }
            } : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            style={clickable ? { cursor: 'pointer' } : undefined}
        >
            <div className={classes.icon}>{icon}</div>
            <div style={{ minWidth: 0 }}>
                <div className={classes.label}>{label}</div>
                <div className={classes.value}>{value}</div>
            </div>
        </article>
    );
}

function sameCurrency(campaigns) {
    const codes = [...new Set(campaigns.map((campaign) => campaign.currency || 'INR'))];
    return {
        mixed: codes.length > 1,
        currency: codes[0] || 'INR',
    };
}

export default function MetaAdsDashboard({ channel = 'meta', onChannel }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const metaCache = useSelector((state) => state.marketingAds?.meta);
    const today = localDate();
    const monthStart = `${today.slice(0, 7)}-01`;
    const mounted = useRef(true);
    const syncLockRef = useRef(false);

    const [from, setFrom] = useState(monthStart);
    const [to, setTo] = useState(today);
    const [city, setCity] = useState('all');
    const [metaPlatform, setMetaPlatform] = useState('both');
    const [zones, setZones] = useState([]);
    const [cities, setCities] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [targets, setTargets] = useState({});
    const [daily, setDaily] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [downloadingReal, setDownloadingReal] = useState(false);
    const [uniqueTotals, setUniqueTotals] = useState(null);
    const [reportVersion, setReportVersion] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [leadDialog, setLeadDialog] = useState(null);
    const [cityDaily, setCityDaily] = useState(null);
    const [cityDailyLoading, setCityDailyLoading] = useState(false);

    const reportCacheKey = metaReportCacheKey(from, to, metaPlatform);
    const hasCachedReport = Boolean(
        metaCache?.cacheKey === reportCacheKey && metaCache?.lastUpdated,
    );

    useEffect(() => () => {
        mounted.current = false;
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        getAdCities({ signal: controller.signal })
            .then((names) => {
                if (!controller.signal.aborted && mounted.current) setCities(names);
            })
            .catch((loadError) => {
                if (isCanceled(loadError) || controller.signal.aborted) return;
            });
        return () => controller.abort();
    }, []);

    const fetchCityDaily = useCallback(async (signal, campaignList) => {
        const ids = (campaignList || [])
            .map((campaign) => campaign.campaignId)
            .filter(Boolean)
            .sort()
            .join(',');
        if (!ids || !from || !to) {
            setCityDaily(null);
            return null;
        }
        setCityDailyLoading(true);
        try {
            const data = await getCityDailyBreakdown({
                from,
                to,
                campaignIds: ids,
                platform: metaPlatform,
                signal,
            });
            if (signal?.aborted || !mounted.current) return null;
            setCityDaily(data);
            return {
                metaCityDaily: data,
                cityDailyCacheKey: metaCityDailyCacheKey(from, to, metaPlatform, ids),
            };
        } catch (cityDailyError) {
            if (isCanceled(cityDailyError) || signal?.aborted) return null;
            if (mounted.current) setCityDaily(null);
            return null;
        } finally {
            if (!signal?.aborted && mounted.current) setCityDailyLoading(false);
        }
    }, [from, to, metaPlatform]);

    const loadReport = useCallback(async (signal, { refreshDaily = true } = {}) => {
        if (!from || !to || from > to || to > today) {
            setError('Select a valid date range.');
            setCampaigns([]);
            setDaily([]);
            setLoaded(false);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const reportPromise = getCampaignReport({ from, to, platform: metaPlatform, signal });
            const targetPromise = getLeadTargets({ signal }).catch((targetError) => {
                if (isCanceled(targetError)) throw targetError;
                return null;
            });
            const [report, nextTargets] = await Promise.all([reportPromise, targetPromise]);
            const nextCampaigns = normalizeCampaigns(report);

            if (signal?.aborted || !mounted.current) return;

            let nextDaily = [];
            if (refreshDaily) {
                const trend = await getSpendTrend({ from, to, platform: metaPlatform, signal });
                if (!signal?.aborted && mounted.current) {
                    nextDaily = normalizeDaily(trend);
                    setDaily(nextDaily);
                }
            }

            const cityDailyPatch = await fetchCityDaily(signal, nextCampaigns);

            if (signal?.aborted || !mounted.current) return;
            setCampaigns(nextCampaigns);
            if (nextTargets) setTargets(nextTargets);
            setLoaded(true);
            setReportVersion((version) => version + 1);

            dispatch(setMarketingAdsMeta({
                cacheKey: reportCacheKey,
                campaigns: nextCampaigns,
                targets: nextTargets || {},
                daily: nextDaily,
                ...(cityDailyPatch || {}),
            }));
            dispatch(setMarketingAdsFilters({ from, to, metaPlatform }));
        } catch (loadError) {
            if (isCanceled(loadError) || signal?.aborted) return;
            if (!mounted.current) return;
            setCampaigns([]);
            setDaily([]);
            setLoaded(false);
            setError(adsErrorMessage(loadError, 'Unable to load the Meta Ads report.'));
        } finally {
            if (!signal?.aborted && mounted.current) setLoading(false);
        }
    }, [from, to, today, metaPlatform, reportCacheKey, dispatch, fetchCityDaily]);

    const zoneCityOptions = useMemo(
        () => citiesMatchingZones(cities, zones),
        [cities, zones],
    );

    useEffect(() => {
        if (city === 'all' || city === '__untagged') return;
        if (zones.length && !zones.includes(zoneForCity(city))) {
            setCity('all');
        }
    }, [city, zones]);

    const loadDaily = useCallback(async (signal) => {
        if (!from || !to || from > to || to > today) {
            setDaily([]);
            return;
        }
        try {
            if (city === '__untagged') {
                const trend = await getSpendTrend({ from, to, city: '__untagged', platform: metaPlatform, signal });
                if (signal?.aborted || !mounted.current) return;
                setDaily(normalizeDaily(trend));
                return;
            }
            if (city !== 'all') {
                const trend = await getSpendTrend({ from, to, city, platform: metaPlatform, signal });
                if (signal?.aborted || !mounted.current) return;
                setDaily(normalizeDaily(trend));
                return;
            }
            if (zones.length) {
                const names = zoneCityOptions;
                if (!names.length) {
                    if (!signal?.aborted && mounted.current) setDaily([]);
                    return;
                }
                const parts = await Promise.all(
                    names.map((name) => getSpendTrend({ from, to, city: name, platform: metaPlatform, signal })),
                );
                if (signal?.aborted || !mounted.current) return;
                setDaily(normalizeDaily(mergeDailyTrends(parts)));
                return;
            }
            const trend = await getSpendTrend({ from, to, platform: metaPlatform, signal });
            if (signal?.aborted || !mounted.current) return;
            setDaily(normalizeDaily(trend));
        } catch (trendError) {
            if (isCanceled(trendError) || signal?.aborted) return;
            if (!mounted.current) return;
            setDaily([]);
        }
    }, [from, to, today, city, zones, zoneCityOptions, metaPlatform]);

    useEffect(() => {
        dispatch(setMarketingAdsFilters({ from, to, metaPlatform }));
    }, [dispatch, from, to, metaPlatform]);

    useEffect(() => {
        if (!hasCachedReport) return;
        setCampaigns(metaCache.campaigns || []);
        setTargets(metaCache.targets || {});
        setDaily(metaCache.daily || []);
        setCityDaily(metaCache.metaCityDaily || null);
        setLoaded(true);
        setLoading(false);
        setError('');
    }, [hasCachedReport, reportCacheKey, metaCache]);

    useEffect(() => {
        if (hasCachedReport || syncing) return undefined;
        if (!from || !to || from > to || to > today) {
            setLoaded(false);
            return undefined;
        }
        const controller = new AbortController();
        loadReport(controller.signal, { refreshDaily: true });
        return () => controller.abort();
    }, [hasCachedReport, syncing, reportCacheKey, from, to, today, loadReport]);

    useEffect(() => {
        if (!loaded) return undefined;
        const controller = new AbortController();
        loadDaily(controller.signal);
        return () => controller.abort();
    }, [loadDaily, loaded]);

    const zoneFilteredCampaigns = useMemo(
        () => campaigns.filter((campaign) => campaignMatchesZones(campaign, zones)),
        [campaigns, zones],
    );

    const visibleCampaigns = useMemo(() => {
        if (city === 'all') return zoneFilteredCampaigns;
        if (city === '__untagged') {
            return zoneFilteredCampaigns.filter((campaign) => !campaignGroupCityTags(campaign).length);
        }
        return zoneFilteredCampaigns.filter((campaign) => campaignGroupCityTags(campaign).includes(city));
    }, [zoneFilteredCampaigns, city]);

    const hasUntagged = !zones.length && campaigns.some((campaign) => !campaignGroupCityTags(campaign).length);
    const cityLabel = city === '__untagged' ? 'Untagged' : city;
    const scopeLabel = useMemo(() => {
        if (city !== 'all') return cityLabel;
        if (zones.length) {
            const names = citiesMatchingZones(cities, zones);
            return names.length ? `${zones.join(', ')} (${names.join(', ')})` : zones.join(', ');
        }
        return 'All cities';
    }, [city, cityLabel, zones, cities]);

    useEffect(() => {
        const controller = new AbortController();
        if (!loaded || !from || !to || from > to || !visibleCampaigns.length) {
            setUniqueTotals(visibleCampaigns.length ? null : { sqlReal: 0, salesTbc: 0, totalReal: 0 });
            return () => controller.abort();
        }
        const ids = visibleCampaigns.map((campaign) => campaign.campaignId).filter(Boolean);
        getMetaAdsBucketTotals({
            from,
            to,
            campaignIds: ids.join(','),
            ...(city !== 'all' && city !== '__untagged' ? { city: cityLabel } : {}),
            platform: metaPlatform,
            signal: controller.signal,
        })
            .then((data) => {
                if (!controller.signal.aborted && mounted.current) setUniqueTotals(data);
            })
            .catch((loadError) => {
                if (isCanceled(loadError) || controller.signal.aborted) return;
                if (mounted.current) setUniqueTotals(null);
            });
        return () => controller.abort();
    }, [loaded, from, to, visibleCampaigns, city, cityLabel, metaPlatform]);

    const { currency, mixed } = useMemo(() => sameCurrency(visibleCampaigns), [visibleCampaigns]);

    const totals = useMemo(() => {
        const sumDaily = (field) => daily.reduce((sum, row) => sum + (row[field] || 0), 0);
        const campaignSpend = visibleCampaigns.reduce((sum, campaign) => sum + campaign.spend, 0);
        const campaignClicks = visibleCampaigns.reduce((sum, campaign) => sum + campaign.clicks, 0);
        const campaignImpressions = visibleCampaigns.reduce(
            (sum, campaign) => sum + campaign.impressions,
            0,
        );
        const scopeUsesDailyTrend = city !== 'all' || zones.length > 0;
        const spend = scopeUsesDailyTrend && daily.length > 0 ? sumDaily('spend') : campaignSpend;
        const clicks = scopeUsesDailyTrend && daily.length > 0 ? sumDaily('clicks') : campaignClicks;
        const impressions = scopeUsesDailyTrend && daily.length > 0
            ? sumDaily('impressions')
            : campaignImpressions;
        const leadsKnown = visibleCampaigns.length > 0 && visibleCampaigns.every((campaign) => campaign.leadCount != null);
        const leadCount = leadsKnown
            ? visibleCampaigns.reduce((sum, campaign) => sum + campaign.leadCount, 0)
            : null;
        const summedSql = visibleCampaigns.reduce((sum, campaign) => sum + (campaign.sqlReal || 0), 0);
        const summedTbc = visibleCampaigns.reduce((sum, campaign) => sum + (campaign.salesTbc || 0), 0);
        const summedTotalReal = visibleCampaigns.reduce((sum, campaign) => sum + (campaign.totalReal || 0), 0);
        const sqlReal = uniqueTotals?.sqlReal != null ? uniqueTotals.sqlReal : summedSql;
        const salesTbc = uniqueTotals?.salesTbc != null ? uniqueTotals.salesTbc : summedTbc;
        const totalReal = uniqueTotals?.totalReal != null ? uniqueTotals.totalReal : summedTotalReal;

        return {
            spend,
            clicks,
            impressions,
            cpc: !mixed && clicks > 0 ? spend / clicks : null,
            leadCount,
            sqlReal,
            salesTbc,
            totalReal,
            costPerReal: !mixed && totalReal > 0 ? spend / totalReal : null,
            crossCampaignOverlap: uniqueTotals?.crossCampaignOverlap || 0,
        };
    }, [visibleCampaigns, mixed, uniqueTotals, daily, city, zones.length]);

    async function handleTargetSave(cityName, leadTarget) {
        try {
            await saveLeadTarget({ city: cityName, leadTarget });
            if (!mounted.current) return;
            setTargets((current) => {
                const next = { ...current };
                if (leadTarget == null) delete next[cityName];
                else next[cityName] = leadTarget;
                dispatch(setMarketingAdsMeta({
                    cacheKey: reportCacheKey,
                    campaigns,
                    targets: next,
                    daily,
                    metaCityDaily: cityDaily,
                    cityDailyCacheKey: metaCache?.cityDailyCacheKey,
                }));
                return next;
            });
            setError('');
        } catch (saveError) {
            if (mounted.current) setError(adsErrorMessage(saveError, 'Unable to save the lead target.'));
            throw saveError;
        }
    }

    async function runMetaSync() {
        if (syncLockRef.current || syncing) return;
        syncLockRef.current = true;
        setSyncing(true);
        setError('');
        setNotice('');
        try {
            const result = await syncCampaignReport({ from, to, incremental: false, full: true });
            if (!mounted.current) return;
            const purged = result?.purged ?? 0;
            setNotice(
                `Meta Sync completed. Cleared ${purged} stored rows and saved ${result?.fetched ?? 0} fresh rows for ${from} to ${to}.`,
            );
            const controller = new AbortController();
            await loadReport(controller.signal, { refreshDaily: true });
        } catch (syncError) {
            if (!mounted.current) return;
            setError(adsErrorMessage(syncError, 'Meta Sync failed. Please try again.'));
        } finally {
            syncLockRef.current = false;
            if (mounted.current) setSyncing(false);
        }
    }

    function exportCsv() {
        const headers = [
            'Campaign ID',
            'Campaign',
            'City',
            'Spend',
            'Currency',
            'Impressions',
            'Clicks',
            'Cost per click',
            'Lead target',
            'Leads received',
            'SQL-Real',
            'Sales-TBC',
            'Total Real',
            '% Achieved',
            'CPERP',
        ];
        const rows = visibleCampaigns.map((campaign) => [
            campaign.campaignId,
            campaign.campaignName,
            campaign.cities?.join(', ') || '',
            campaign.spend,
            campaign.currency,
            campaign.impressions,
            campaign.clicks,
            campaign.costPerClick ?? '',
            (city === 'all'
                ? (campaign.cities || []).map((name) => targets[name]).filter((value) => value != null).join(', ')
                : (targets[cityLabel] ?? '')),
            campaign.leadCount ?? 0,
            campaign.sqlReal ?? 0,
            campaign.salesTbc ?? 0,
            campaign.totalReal ?? 0,
            (() => {
                const name = city === 'all' ? campaign.cities?.[0] : cityLabel;
                const target = name ? targets[name] : null;
                if (!target) return '';
                const rows = visibleCampaigns.filter((item) => (item.cities?.length ? item.cities : ['Untagged']).includes(name));
                const real = rows.reduce((sum, item) => sum + (item.totalReal || 0), 0);
                return Math.round((real / target) * 100);
            })(),
            campaign.costPerLead ?? '',
        ]);
        const csv = [headers, ...rows]
            .map((row) => row.map(escapeCsv).join(','))
            .join('\r\n');
        const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        const cityFile = city === 'all' ? 'all-cities' : cityLabel.toLowerCase().replace(/\s+/g, '-');
        link.download = `meta-ads-${cityFile}-${from}-to-${to}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }

    function openLeads(bucket, campaignIds, label, day) {
        setLeadDialog({
            title: `${LEAD_BUCKET_LABEL[bucket] || 'Leads'} · ${label}`,
            query: {
                from: day || from,
                to: day || to,
                bucket,
                ...(campaignIds?.length
                    ? { campaignIds: campaignIds.join(',') }
                    : {}),
                ...(city !== 'all' && city !== '__untagged' && !day ? { city: cityLabel } : {}),
                ...(metaPlatform && metaPlatform !== 'both' ? { platform: metaPlatform } : {}),
            },
        });
    }

    const money = (value) => {
        if (!loaded || mixed) return '—';
        return formatMetric(value, (amount) => formatMoney(amount, currency));
    };
    const campaignIds = useMemo(
        () => visibleCampaigns.map((campaign) => campaign.campaignId).filter(Boolean),
        [visibleCampaigns],
    );
    const showPageLoader = syncing || (loading && !hasCachedReport);
    const loaderPhase = syncing && !loading ? 'sync' : 'campaigns';

    async function downloadTotalReal() {
        if (downloadingReal || !visibleCampaigns.length) return;
        setDownloadingReal(true);
        setError('');
        try {
            const data = await getMetaAdsLeadDetails({
                from,
                to,
                bucket: 'real',
                platform: metaPlatform,
                ...(city === 'all'
                    ? { campaignIds: visibleCampaigns.map((campaign) => campaign.campaignId).filter(Boolean).join(',') }
                    : { city: cityLabel }),
            });
            const rows = Array.isArray(data?.leads) ? data.leads : [];
            if (!rows.length) {
                setError('No Total Real leads found for this selection.');
                return;
            }
            downloadLeadRowsCsv(`Total Real · ${scopeLabel} · ${from}-to-${to}`, rows);
        } catch (downloadError) {
            setError(adsErrorMessage(downloadError, 'Unable to download Total Real leads.'));
        } finally {
            if (mounted.current) setDownloadingReal(false);
        }
    }

    return (
        <section className={classes.root}>
            <div className={classes.page}>
                <header className={classes.headerCard}>
                    <div>
                        <LeadChannelSwitch value={channel} onChange={onChannel} />
                        <MetaPlatformSwitch value={metaPlatform} onChange={setMetaPlatform} />
                        <h1 className={classes.title}>Meta Ads Expenditure</h1>
                        <p className={classes.subtitle}>
                            Campaign spend for the selected dates, with leads counted from Meta lead records.
                            {' '}Filter by Facebook, Instagram, or Both under Meta platform.
                        </p>
                    </div>
                    <div className={classes.actions}>
                        <Button
                            className={classes.syncBtn}
                            variant="contained"
                            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                            onClick={runMetaSync}
                            disabled={syncing || loading || !from || !to || from > to || to > today}
                        >
                            {syncing ? 'Syncing...' : 'Meta Sync'}
                        </Button>
                        <Button
                            className={classes.exportBtn}
                            variant="contained"
                            startIcon={<GetAppIcon />}
                            onClick={exportCsv}
                            disabled={!visibleCampaigns.length || loading}
                        >
                            Export CSV
                        </Button>
                        <Button
                            className={classes.exportBtn}
                            variant="contained"
                            startIcon={downloadingReal ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
                            onClick={downloadTotalReal}
                            disabled={!visibleCampaigns.length || loading || downloadingReal || !totals.totalReal}
                        >
                            {downloadingReal ? 'Downloading...' : 'Download Total Real'}
                        </Button>
                    </div>
                </header>

                <div className={classes.filters}>
                    <TextField
                        label="From"
                        type="date"
                        size="small"
                        variant="outlined"
                        value={from}
                        inputProps={{ max: to < today ? to : today }}
                        onChange={(event) => setFrom(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="To"
                        type="date"
                        size="small"
                        variant="outlined"
                        value={to}
                        inputProps={{ min: from, max: today }}
                        onChange={(event) => setTo(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <FormControl
                        className={classes.zoneField}
                        variant="outlined"
                        size="small"
                    >
                        <InputLabel id="meta-ads-zone-label">Zone</InputLabel>
                        <Select
                            labelId="meta-ads-zone-label"
                            multiple
                            value={zones}
                            onChange={(event) => setZones(event.target.value)}
                            label="Zone"
                            renderValue={(selected) => (
                                selected.length ? selected.join(', ') : 'All zones'
                            )}
                        >
                            {ZONES.map((name) => (
                                <MenuItem key={name} value={name}>
                                    <Checkbox checked={zones.indexOf(name) > -1} color="primary" />
                                    <ListItemText primary={name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        className={classes.cityField}
                        select
                        label="City"
                        size="small"
                        variant="outlined"
                        value={city}
                        onChange={(event) => setCity(event.target.value)}
                    >
                        <MenuItem value="all">All cities</MenuItem>
                        {zoneCityOptions.map((name) => (
                            <MenuItem key={name} value={name}>{name}</MenuItem>
                        ))}
                        {hasUntagged && <MenuItem value="__untagged">Untagged</MenuItem>}
                    </TextField>
                </div>

                {error && (
                    <div className={`${classes.alert} ${classes.error}`} role="alert">{error}</div>
                )}
                {notice && (
                    <div className={`${classes.alert} ${classes.success}`} role="status">{notice}</div>
                )}

                {showPageLoader ? (
                    <div className={classes.loaderCard}>
                        <MetaAdsCampaignLoader active phase={loaderPhase} />
                    </div>
                ) : (
                    <>
                <MarketingSpendOverviewAccordion
                    title="Spend summary & daily chart"
                    hint={mixed ? 'Multiple currencies' : `Spend ${formatMoney(totals.spend, currency)} · Total Real ${formatNumber(totals.totalReal)}`}
                >
                <div className={classes.grid}>
                    <SummaryCard
                        classes={classes}
                        icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
                        label="Total spend"
                        value={mixed ? 'Multiple currencies' : formatMoney(totals.spend, currency)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<VisibilityOutlinedIcon fontSize="small" />}
                        label="Impressions"
                        value={formatNumber(totals.impressions)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<TouchAppIcon fontSize="small" />}
                        label="Clicks"
                        value={formatNumber(totals.clicks)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<MonetizationOnOutlinedIcon fontSize="small" />}
                        label="Cost per click"
                        value={money(totals.cpc)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<TouchAppIcon fontSize="small" />}
                        label="Leads received"
                        value={formatNumber(totals.leadCount)}
                        onClick={() => openLeads('received', campaignIds, scopeLabel)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<TouchAppIcon fontSize="small" />}
                        label="SQL-Real"
                        value={formatNumber(totals.sqlReal)}
                        onClick={() => openLeads('sql', campaignIds, scopeLabel)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<TouchAppIcon fontSize="small" />}
                        label="Sales-TBC"
                        value={formatNumber(totals.salesTbc)}
                        onClick={() => openLeads('tbc', campaignIds, scopeLabel)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<TouchAppIcon fontSize="small" />}
                        label="Total Real"
                        value={formatNumber(totals.totalReal)}
                        onClick={() => openLeads('real', campaignIds, scopeLabel)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<MonetizationOnOutlinedIcon fontSize="small" />}
                        label="CPERP"
                        value={money(totals.costPerReal)}
                    />
                </div>
                <p className={classes.footnote}>
                    Leads received use the Meta form date in the selected range.
                    {' '}SQL-Real and Sales-TBC use the lead assigned date (Sales-TBC uses form date when assign date is missing).
                    {' '}Junk, Hold, and Move to Marketing are excluded. Total Real is SQL-Real plus Sales-TBC.
                    {' '}Sales Won uses static sales won amount and stays on the form received date even if the deal closes later. CPERP is spend divided by Total Real.
                    {' '}Total spend, impressions, and clicks follow the daily Meta sync for your date range and city/zone filter (same as the chart), not a raw sum of campaign rows.
                    {' '}Click a summary count or a day on the chart to open those leads.
                    {totals.crossCampaignOverlap > 0
                        ? ` ${formatNumber(totals.crossCampaignOverlap)} leads appear in more than one campaign in this view.`
                        : ''}
                </p>
                <SpendTrendChart
                    daily={daily}
                    currency={currency}
                    mixedCurrency={mixed}
                    onDayClick={(date) => openLeads('received', campaignIds, date, date)}
                    emptyMessage="No daily spend for this range. Run Meta Sync, or wait until the reporting API returns daily rows."
                />
                </MarketingSpendOverviewAccordion>
                <CampaignSpendTable
                    key={`${from}-${to}-${metaPlatform}-${reportVersion}`}
                    campaigns={visibleCampaigns}
                    metaPlatform={metaPlatform}
                    loading={loading && !hasCachedReport}
                    syncing={syncing}
                    from={from}
                    to={to}
                    cityDailyData={cityDaily}
                    cityDailyLoading={cityDailyLoading}
                    hasError={Boolean(error)}
                    scopeLabel={scopeLabel}
                    groupCity={city !== 'all' ? cityLabel : null}
                    targets={targets}
                    onSaveTarget={handleTargetSave}
                    uniqueTotals={uniqueTotals}
                    emptyMessage={(() => {
                        if (city !== 'all') {
                            return `No campaigns tagged to ${cityLabel} in this date range.`;
                        }
                        if (zones.length) {
                            return `No campaigns in ${zones.join(', ')} for this date range.`;
                        }
                        return '';
                    })()}
                />
                    </>
                )}
                <MetaAdsLeadDialog
                    open={Boolean(leadDialog)}
                    title={leadDialog?.title || 'Leads'}
                    query={leadDialog?.query}
                    onClose={() => setLeadDialog(null)}
                    loadLeads={getMetaAdsLeadDetails}
                    createdDateLabel="Meta created date"
                />
            </div>
        </section>
    );
}
